import clsx from 'clsx'
import { InfoIcon, Loader2Icon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { toast } from 'sonner'

import { Badge, Tooltip } from 'slab'
import { capitalize, formatMoney } from 'utils'

import { useOnboarding } from '../OnboardingContext'
import { type OnboardingEnrollment, type Step } from '../types'
import EnrollmentSidebarCardDropdown from './EnrollmentSidebarCardDropdown'
import { OnboardingMetalTierBadge } from './OnboardingMetalTierBadge/OnboardingMetalTierBadge'
import { PlanBadge } from './PlanBadge/PlanBadge'
import { PlanCard } from './PlanCard'
import { PlanChip } from './PlanChip'

const getLabel = (enrollment: OnboardingEnrollment) => {
  if (enrollment.covers_medical) {
    return 'Medical'
  } else if (enrollment.covers_vision) {
    return 'Vision'
  } else {
    return 'Dental'
  }
}

const getMedicalBodyText = (
  enrollment: OnboardingEnrollment,
  formattedPremium: string,
  onRemovePlan: () => void,
  isAtFinalizeEnrollment: boolean,
  navigateToSpecificStep?: (step: Step) => void,
  disableChangePlanSelection?: boolean
) => {
  return (
    <div className='flex flex-col gap-4'>
      <div className='flex justify-between items-start gap-2'>
        <PlanChip
          carrierLogo={enrollment.carrier_image_url ?? ''}
          carrierName={enrollment.carrier_name ?? ''}
          planName={capitalize(enrollment.display_name ?? '')}
        />
        {!disableChangePlanSelection && (
          <div className='flex w-4 h-4 items-center justify-center'>
            <EnrollmentSidebarCardDropdown
              enrollment={enrollment}
              onRemovePlan={isAtFinalizeEnrollment ? undefined : onRemovePlan}
              navigateToSpecificStep={navigateToSpecificStep}
            />
          </div>
        )}
      </div>
      <div
        className={clsx('flex justify-between gap-x-2', {
          'items-center':
            isAtFinalizeEnrollment ||
            enrollment.medical_metal_tier !== 'expanded_bronze',
          'items-end':
            !isAtFinalizeEnrollment &&
            enrollment.medical_metal_tier === 'expanded_bronze',
        })}
      >
        <div
          className={clsx('flex', {
            'flex-col gap-y-1 items-start':
              !isAtFinalizeEnrollment &&
              enrollment.medical_metal_tier === 'expanded_bronze',
            'gap-x-2 items-center':
              isAtFinalizeEnrollment ||
              enrollment.medical_metal_tier !== 'expanded_bronze',
          })}
        >
          {enrollment.medical_metal_tier && (
            <OnboardingMetalTierBadge tier={enrollment.medical_metal_tier} />
          )}
          {enrollment.medical_plan_type && (
            <PlanBadge type={enrollment.medical_plan_type} />
          )}
          {enrollment.covers_medical &&
            enrollment.payment_method !== 'paid_by_thatch' && (
              <Tooltip content="Thatch won't enroll you or make any payments for this plan">
                <Badge type='info'>
                  <div className='flex'>
                    Purchased by you <InfoIcon className='ml-1 w-4 h-4' />
                  </div>
                </Badge>
              </Tooltip>
          )}
        </div>
        <div>{formattedPremium}</div>
      </div>
    </div>
  )
}

const getNonMedicalBodyText = (
  enrollment: OnboardingEnrollment,
  formattedPremium: string,
  onRemovePlan: () => void,
  navigateToSpecificStep?: (step: Step) => void,
  disableChangePlanSelection?: boolean
) => {
  return (
    <div className='flex flex-col gap-4'>
      <div className='flex justify-between items-start gap-4'>
        <PlanChip
          carrierLogo={enrollment.carrier_image_url ?? ''}
          carrierName={enrollment.carrier_name ?? ''}
          planName={capitalize(enrollment.plan_code ?? '')}
        />
        <div className='flex flex-col gap-2 items-end'>
          {!disableChangePlanSelection && (
            <div className='flex w-4 h-4 items-center justify-center'>
              <EnrollmentSidebarCardDropdown
                enrollment={enrollment}
                onRemovePlan={onRemovePlan}
                navigateToSpecificStep={navigateToSpecificStep}
              />
            </div>
          )}
          <div>{formattedPremium}</div>
        </div>
      </div>
    </div>
  )
}

const EnrollmentSidebarCard = ({
  enrollment,
  navigateToSpecificStep,
  isAtFinalizeEnrollment,
}: {
  enrollment: OnboardingEnrollment;
  navigateToSpecificStep?: (step: Step) => void;
  isAtFinalizeEnrollment: boolean;
}) => {
  const { onboardingData, deleteEnrollment, refreshOnboardingInfo } =
    useOnboarding()
  const deleteMutation = deleteEnrollment(enrollment.id || '')
  const label = getLabel(enrollment)

  // If this is a renewal event (based on plan change summary), and
  // if the dental or vision plan ends mid year, then
  // the user should not be able to modify dental or vision plan selection
  let disableChangePlanSelection = false
  if (enrollment.covers_dental || enrollment.covers_vision) {
    if (enrollment.covers_dental) {
      if (
        onboardingData?.restrictions?.dental_not_available ===
        'coverage_ends_mid_year'
      ) {
        disableChangePlanSelection = true
      }
    } else if (enrollment.covers_vision) {
      if (
        onboardingData?.restrictions?.vision_not_available ===
        'coverage_ends_mid_year'
      ) {
        disableChangePlanSelection = true
      }
    }
  } else if (enrollment.category === 'medicare') {
    disableChangePlanSelection = true
  }

  const onRemovePlan = () => {
    deleteMutation.mutate(undefined, {
      onError: () => {
        toast.error(
          'There has been an error removing the plan, please try again later.'
        )
      },
    })
  }

  const formattedPremium =
    typeof enrollment.quoted_premium === 'number'
      ? formatMoney(enrollment.quoted_premium)
      : enrollment.quoted_premium?.formatted_amount ?? ''

  const bodyText = enrollment.covers_medical
    ? getMedicalBodyText(
      enrollment,
      formattedPremium,
      onRemovePlan,
      isAtFinalizeEnrollment,
      navigateToSpecificStep,
      disableChangePlanSelection
    )
    : getNonMedicalBodyText(
      enrollment,
      formattedPremium,
      onRemovePlan,
      navigateToSpecificStep,
      disableChangePlanSelection
    )

  return (
    <AnimatePresence onExitComplete={() => refreshOnboardingInfo()}>
      {!deleteMutation.isSuccess && (
        <motion.div
          className='mb-4 last:mb-0'
          exit={{ overflow: 'hidden', height: 0, marginBottom: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h2
            className={clsx('body-bold text-gray-800 pb-2 transition-opacity', {
              'opacity-50':
                deleteMutation.isPending || deleteMutation.isSuccess,
            })}
          >
            {label}
          </h2>
          <div className='grid'>
            <div
              className={clsx(
                'col-start-1 row-start-1 opacity-100 transition-opacity',
                {
                  'opacity-50':
                    deleteMutation.isPending || deleteMutation.isSuccess,
                }
              )}
            >
              <PlanCard data-testid={`enrollment-card-${label.toLowerCase()}`}>
                <PlanCard.Section>{bodyText}</PlanCard.Section>
              </PlanCard>
            </div>
            {deleteMutation.isPending && (
              <div className='col-start-1 row-start-1 z-10'>
                <div
                  data-testid='enrollment-sidebar-loading-spinner'
                  className='flex justify-center items-center h-full'
                >
                  <Loader2Icon
                    size={24}
                    className='animate-spin text-thatch-500'
                  />
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default EnrollmentSidebarCard
